import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext.js";
import "./Settings.css";

const API_URL = process.env.REACT_APP_API_URL;

const Settings = ({ onNavigate }) => {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingUserSettings, setSavingUserSettings] = useState(false);
  const [capacityByGroup, setCapacityByGroup] = useState({});
  const [defaultMaxStudents, setDefaultMaxStudents] = useState(4);
  const [minStudentsToRun, setMinStudentsToRun] = useState(1);
  const [allowResetSchedule, setAllowResetSchedule] = useState(true);

  useEffect(() => {
    fetchSettings();
    fetchUserSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      setSettings(response.data);
      setDefaultMaxStudents(response.data.courseCapacity.defaultMaxStudents);
      setMinStudentsToRun(response.data.courseCapacity.minStudentsToRun);
      setCapacityByGroup(response.data.courseCapacity.capacityByGroup);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Fehler beim Laden der Einstellungen");
      setLoading(false);
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user-settings`);
      setUserSettings(response.data);
      setAllowResetSchedule(response.data.allowResetSchedule);
    } catch (error) {
      console.error("Error fetching user settings:", error);
      toast.error("Fehler beim Laden der Benutzereinstellungen");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/settings`, {
        courseCapacity: {
          defaultMaxStudents: parseInt(defaultMaxStudents),
          minStudentsToRun: parseInt(minStudentsToRun),
          capacityByGroup: Object.keys(capacityByGroup).reduce((acc, key) => {
            acc[key] = parseInt(capacityByGroup[key]);
            return acc;
          }, {})
        }
      });
      setSettings(response.data);
      toast.success("Einstellungen erfolgreich gespeichert");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Fehler beim Speichern der Einstellungen");
    } finally {
      setSaving(false);
    }
  };

  const handleGroupCapacityChange = (group, value) => {
    setCapacityByGroup({
      ...capacityByGroup,
      [group]: value
    });
  };

  const handleUserSettingsSave = async () => {
    setSavingUserSettings(true);
    try {
      const response = await axios.put(`${API_URL}/api/user-settings`, {
        allowResetSchedule
      });
      setUserSettings(response.data.settings);
      toast.success("Benutzereinstellungen erfolgreich gespeichert");
    } catch (error) {
      console.error("Error saving user settings:", error);
      toast.error("Fehler beim Speichern der Benutzereinstellungen");
    } finally {
      setSavingUserSettings(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <h1>⚙️ Einstellungen</h1>
        <p>Lädt...</p>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>⚙️ Einstellungen</h1>
        <p className="settings-description">
          Konfigurieren Sie die Parameter für die automatische Kursplanung
        </p>
      </div>

      <div className="settings-content">
        {/* User Management - Admin only */}
        {isAdmin && (
          <div className="settings-section">
            <h2>👥 Benutzerverwaltung</h2>
            <p className="section-description">
              Verwalten Sie Benutzerkonten für Trainer und Schüler
            </p>
            <div className="settings-card">
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  className="navigation-button"
                  onClick={() => onNavigate && onNavigate("userManagement")}
                >
                  👥 Zur Benutzerverwaltung
                </button>
                <button
                  className="navigation-button"
                  onClick={() => onNavigate && onNavigate("loginActivity")}
                >
                  🟢 Wer ist online?
                </button>
              </div>
            </div>

            <div className="settings-card">
              <div className="setting-row">
                <div className="setting-info">
                  <label htmlFor="allowResetSchedule">
                    Plan-Generieren-Button aktivieren
                  </label>
                  <span className="setting-hint">
                    Aktivieren/Deaktivieren Sie den "Plan generieren" Button in der Kursplanung
                  </span>
                </div>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="allowResetSchedule"
                    name="allowResetSchedule"
                    checked={allowResetSchedule}
                    onChange={(e) => setAllowResetSchedule(e.target.checked)}
                  />
                  <span className="toggle-label">
                    {allowResetSchedule ? "Ja" : "Nein"}
                  </span>
                </div>
              </div>

              <div className="settings-actions">
                <button
                  className="save-button"
                  onClick={handleUserSettingsSave}
                  disabled={savingUserSettings}
                >
                  {savingUserSettings ? "Speichert..." : "💾 Benutzereinstellungen speichern"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Course Capacity Settings */}
        <div className="settings-section">
          <h2>📊 Kurskapazität</h2>
          <p className="section-description">
            Legen Sie fest, wie viele Schüler maximal in einem Kurs sein können
          </p>

          <div className="settings-card">
            <div className="setting-row">
              <div className="setting-info">
                <label htmlFor="defaultMaxStudents">
                  Standard-Maximum Schüler pro Kurs
                </label>
                <span className="setting-hint">
                  Standardwert wenn keine spezifische Gruppenkapazität festgelegt ist
                </span>
              </div>
              <input
                type="number"
                id="defaultMaxStudents"
                min="1"
                max="10"
                value={defaultMaxStudents}
                onChange={(e) => setDefaultMaxStudents(e.target.value)}
              />
            </div>

            <div className="setting-row">
              <div className="setting-info">
                <label htmlFor="minStudentsToRun">
                  Minimum Schüler pro Kurs
                </label>
                <span className="setting-hint">
                  Minimale Anzahl Schüler, damit ein Kurs stattfindet
                </span>
              </div>
              <input
                type="number"
                id="minStudentsToRun"
                min="1"
                max="5"
                value={minStudentsToRun}
                onChange={(e) => setMinStudentsToRun(e.target.value)}
              />
            </div>
          </div>

          <div className="settings-card">
            <h3>Kapazität nach Trainingsgruppe</h3>
            <p className="card-description">
              Passen Sie die maximale Schüleranzahl für jede Trainingsgruppe individuell an
            </p>

            <div className="capacity-grid">
              {Object.entries(capacityByGroup).map(([group, capacity]) => (
                <div key={group} className="capacity-item">
                  <label htmlFor={`capacity-${group}`}>
                    {group === 'Gelb Team' ? '🎾' :
                     group === 'Gelb Hobby' ? '🎾' :
                     group === 'Kinderland' ? '👶' :
                     group === 'Rot' ? '🔴' :
                     group === 'Orange' ? '🟠' :
                     group === 'Grün' ? '🟢' :
                     '👥'} {group}
                  </label>
                  <input
                    type="number"
                    id={`capacity-${group}`}
                    min="1"
                    max="10"
                    value={capacity}
                    onChange={(e) => handleGroupCapacityChange(group, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="settings-actions">
            <button
              className="save-button"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Speichert..." : "💾 Einstellungen speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
